import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import StatusReportFilters from './components/StatusReportFilters';
import StatusReportDataTable from './components/StatusReportDataTable';
import type { StatusReportSummaryDTO } from './models/statusReportDTOs';
import type { StatusReportFilterState } from './models/statusReportFilterDTO';
import { useAuth } from '@/src/app/context/useAuth';

// Status Report page: fetch, filter, and display the status report

const StatusReportPage: React.FC = () => {
    document.title = 'Status Report';
    // Page state
    const [data, setData] = useState<Array<StatusReportSummaryDTO>>([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [examName, setExamName] = useState(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) return JSON.parse(saved)?.examName || '';
        } catch {
            // intentionally empty
        }
        return '';
    });
    const pendingExamNameRef = useRef('');

    const [filterState, setFilterState] = useState<StatusReportFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    groupBy: parsed.groupBy || 'examClass',
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            groupBy: 'examClass',
            examTypeMasterId: 0,
            examId: 0,
        };
    });

    const [appliedFilters, setAppliedFilters] = useState<StatusReportFilterState>(filterState);

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                groupBy: filterState.groupBy,
                examTypeMasterId: filterState.examTypeMasterId,
                examId: filterState.examId,
                examIds: [filterState.examId],
                examName,
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, examName, userId, UNIVERSAL_FILTER_KEY]);

    // Load table data using current or provided filters
    const refreshTableData = useCallback(
        async (filters: StatusReportFilterState = filterState) => {
            const {
                gradeLevelMasterId,
                districtId,
                schoolId,
                classId,
                groupBy,
                examTypeMasterId,
                examId,
            } = filters;

            setHasSearched(true);
            setError('');
            setLoaded(false);

            if (!districtId || !schoolId || !gradeLevelMasterId) {
                setError('Please select District, School, and Grade.');
                setLoaded(true);
                return;
            }

            let requestUrl = `reports/statusReport?districtId=${districtId}`;
            requestUrl += `&schoolId=${schoolId}`;
            requestUrl += `&gradeId=${gradeLevelMasterId}`;
            requestUrl += `&classId=${classId}`;
            requestUrl += `&groupBy=${groupBy}`;
            requestUrl += `&examTypeMasterId=${examTypeMasterId}`;
            requestUrl += `&examId=${examId}`;

            try {
                const res = await apiRequest<Array<StatusReportSummaryDTO>>('GET', requestUrl);
                setData(res.data ?? []);
                setAppliedFilters(filters);
                if (pendingExamNameRef.current) setExamName(pendingExamNameRef.current);
            } catch {
                setError('Failed to load status report');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    useEffect(() => {
        const fs = filterState;
        if (fs.districtId && fs.schoolId && fs.gradeLevelMasterId) {
            refreshTableData(fs);
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Render page
    return (
        <Container>
            <div>
                <div>
                    <h1 className="page-header">Status Report</h1>
                    <br />

                    <div>
                        <Box>
                            <StatusReportFilters
                                filterState={filterState}
                                setFilterState={setFilterState}
                                refreshTableData={refreshTableData}
                                onExamNameChange={(name) => {
                                    pendingExamNameRef.current = name;
                                }}
                            />
                        </Box>
                    </div>

                    {examName && (
                        <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>
                            {examName}
                        </h2>
                    )}

                    <Loading visible={hasSearched && !loaded} message="Loading Status Report..." />
                    <ErrorMessage error={hasSearched ? error : ''} />

                    {hasSearched && loaded && (
                        <div>
                            <div>
                                <StatusReportDataTable
                                    data={data}
                                    groupBy={appliedFilters.groupBy}
                                    selectedClassId={appliedFilters.classId}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Container>
    );
};

export default StatusReportPage;
